// Dates d'activation des phases par le nouveau suivi, pour l'affichage et la
// semaine du parcours. Résolues pour TOUTE la cliente (versions + vue
// suivi_versions), par identifiant de phase : la version affichée n'a pas à
// être celle qui a changé de phase.

import { useEffect, useState } from 'react';
import { supabase as defaultDb } from '../../supabaseClient';
import { datesActivationPhases, dateActivationSuivi, phaseCibleSuivi } from './suiviPhaseDates';

/** Première sortie (publication ou remise) d'une ligne de suivi_versions. */
export function premiereSortie(version) {
  const dates = [version?.publiee_le, version?.publication_tampon_le, version?.remise_le]
    .filter(Boolean)
    .map((value) => ({ value, time: new Date(value).getTime() }))
    .filter((item) => Number.isFinite(item.time))
    .sort((a, b) => a.time - b.time);
  return dates[0]?.value ?? null;
}

/** Lit les dates d'activation de toutes les phases d'une cliente ({} si rien). */
export async function lireDatesActivationPhases(clientId, { db = defaultDb } = {}) {
  if (!clientId || !db?.from) return {};
  const [lignes, sorties] = await Promise.all([
    db.from('nutrition_consultations')
      .select('id,suivi_motif,followup_data,client_app_publication')
      .eq('client_id', clientId)
      .eq('is_deleted', false),
    db.from('suivi_versions')
      .select('consultation_id,publiee_le,publication_tampon_le,remise_le')
      .eq('client_id', clientId),
  ]);
  if (lignes?.error || sorties?.error) return {};
  return datesActivationPhases(lignes?.data || [], sorties?.data || []);
}

/**
 * Pour la génération du plan : au plus `delai` ms, sinon null (comportement
 * d'avant, la génération n'attend jamais).
 */
export function lireDatesActivationPhasesVite(clientId, { db = defaultDb, delai = 1500 } = {}) {
  let minuterie;
  const delaiDepasse = new Promise((resolve) => { minuterie = setTimeout(() => resolve(null), delai); });
  return Promise.race([
    lireDatesActivationPhases(clientId, { db }).catch(() => null),
    delaiDepasse,
  ]).then((dates) => { clearTimeout(minuterie); return dates; });
}

function phaseActiveSansDate(consultation) {
  const active = consultation?.protocol_phases?.phases?.find?.((phase) => phase?.status === 'active');
  return Boolean(active && !active.started_at);
}

/**
 * Table { phaseId: date } pour la consultation affichée. Rien n'est lu tant
 * que la phase active a déjà une date stockée (ancien circuit inchangé).
 */
export default function useDateActivationSuivi(consultation, { db = defaultDb } = {}) {
  const clientId = consultation?.client_id ?? consultation?.clientId ?? null;
  const aLire = phaseActiveSansDate(consultation) && clientId ? String(clientId) : null;
  const [lue, setLue] = useState({ id: null, dates: {} });

  useEffect(() => {
    if (!aLire) return undefined;
    let actif = true;
    lireDatesActivationPhases(aLire, { db })
      .catch(() => ({}))
      .then((dates) => { if (actif) setLue({ id: aLire, dates }); });
    return () => { actif = false; };
  }, [aLire, db]);

  const dates = lue.id === aLire ? { ...lue.dates } : {};
  // Publication de la version affichée : connue tout de suite.
  const connue = dateActivationSuivi(consultation);
  const cible = phaseCibleSuivi(consultation);
  if (connue && cible && !dates[cible]) dates[cible] = connue;
  return dates;
}
